import { useState, useCallback, useMemo } from 'react';
import Raven from 'raven-js';
import { useApolloClient } from '@apollo/client';
import { getDealId } from 'quotes-ui-lib/utils/quote';
import { EDIT } from 'quotes-modal-lib/constants/Flows';
import { Flex } from 'foundations-components/components/Flex';
import QuotesIFrameModal from 'quotes-modal-lib/QuotesIFrameModal';
import InvoicesEditorIFrameModal, { InvoiceOperation } from 'invoices-iframe-lib/components/InvoicesEditorIFrameModal';
import { getFullQuoteUrlViaDeal as getFullQuoteUrl } from 'quotes-ui-lib/utils/urls';
import { QUOTE_HIGHLIGHT_QUERY } from '../graphQL/quoteHighlightQuery';
import { QUOTE_TYPE_ID, INVOICE_TYPE_ID, COMPANY_TYPE_ID, CONTACT_TYPE_ID, LINE_ITEM_TYPE_ID, DEAL_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { CPQ_QUOTE_TRACKING_M1_GATE, INVOICE_CREATE_SCOPE, SEAMLESS_SENDING_GATE, SUBSCRIPTIONS_WRITE_SCOPE, AI_GENERATE_BUYER_EMAIL_GATE } from 'quotes-ui-lib/utils/permissions';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { UPDATE_OBJECT, UPDATE_ASSOCIATIONS } from 'crm-message-bus/types/MessageTypes';
import ActionsDropdown from 'quotes-ui-lib/components/ActionsDropdown';
import { EMBED_SOURCE } from 'quotes-modal-lib/constants/embedSource';
import { useHasCPQQuoteAccess } from 'growth-payments-core/auth/gates';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultFramedEditorState = {
  open: false,
  quoteId: null
};
const TRACKING_LOCATION = 'quotePreviewPanel';
const QuoteHighlightActions = ({
  quote,
  quoteEsign,
  userEmail,
  userOwnerId,
  canCurrentUserEditQuote,
  scopes,
  gates,
  closeSidebar
}) => {
  const fullQuoteUrl = getFullQuoteUrl(quote);
  const quoteSigners = quoteEsign ? quoteEsign.signers : null;
  const client = useApolloClient();
  const sendUpdateObjectAction = useSendCrmMessageTopic(UPDATE_OBJECT);
  const sendUpdateAssociationsAction = useSendCrmMessageTopic(UPDATE_ASSOCIATIONS);
  const [quoteEditorState, setQuoteEditorState] = useState(DefaultFramedEditorState);
  const [invoiceEditorState, setInvoiceEditorState] = useState(DefaultFramedEditorState);
  const isScopedForInvoices = useMemo(() => {
    return scopes.includes(INVOICE_CREATE_SCOPE);
  }, [scopes]);
  const isScopedForBilling = useMemo(() => scopes.includes(SUBSCRIPTIONS_WRITE_SCOPE), [scopes]);
  const isUngatedForSeamlessSending = useMemo(() => gates.includes(SEAMLESS_SENDING_GATE), [gates]);
  const hasCPQAccess = useHasCPQQuoteAccess();
  const isUngatedForQuoteTracking = useMemo(() => hasCPQAccess && gates.includes(CPQ_QUOTE_TRACKING_M1_GATE), [hasCPQAccess, gates]);
  const isUngatedForAiGenerateBuyerEmail = useMemo(() => hasCPQAccess && gates.includes(AI_GENERATE_BUYER_EMAIL_GATE), [hasCPQAccess, gates]);
  const showInvoiceEditor = quoteId => {
    setInvoiceEditorState({
      open: true,
      quoteId
    });
  };
  const showQuoteEditor = quoteId => {
    setQuoteEditorState({
      open: true,
      quoteId
    });
  };
  const handleQuotePropertyUpdate = () => {
    client.refetchQueries({
      include: [QUOTE_HIGHLIGHT_QUERY]
    }).catch(e => {
      Raven.captureException(e);
    });
  };
  const handleQuoteDelete = quoteId => {
    closeSidebar();
    sendUpdateObjectAction({
      objectTypeId: QUOTE_TYPE_ID,
      objectId: quoteId
    });
  };
  const handleQuoteEditorClose = useCallback(() => {
    const quoteId = quoteEditorState.quoteId;
    setQuoteEditorState({
      open: false,
      quoteId: null
    });
    const dealId = String(getDealId(quote));
    client.refetchQueries({
      include: [QUOTE_HIGHLIGHT_QUERY]
    }).catch(() => {});
    sendUpdateObjectAction({
      objectTypeId: QUOTE_TYPE_ID,
      objectId: quoteId || ''
    });
    sendUpdateAssociationsAction({
      objectTypeId: COMPANY_TYPE_ID,
      objectId: quoteId || ''
    });
    sendUpdateAssociationsAction({
      objectTypeId: CONTACT_TYPE_ID,
      objectId: quoteId || ''
    });
    sendUpdateAssociationsAction({
      objectTypeId: LINE_ITEM_TYPE_ID,
      objectId: ''
    });
    sendUpdateObjectAction({
      objectTypeId: DEAL_TYPE_ID,
      objectId: dealId
    });
  }, [client, quote, quoteEditorState, sendUpdateAssociationsAction, sendUpdateObjectAction]);
  const handleInvoiceEditorClose = useCallback(() => {
    setInvoiceEditorState({
      open: false,
      quoteId: null
    });
    sendUpdateObjectAction({
      objectTypeId: INVOICE_TYPE_ID,
      objectId: '' // Intentionally left blank
    });
    // todo: refetch other objects in the sidebar
  }, [sendUpdateObjectAction]);
  const renderQuoteEditor = () => {
    return /*#__PURE__*/_jsx(QuotesIFrameModal, {
      show: quoteEditorState.open,
      flowType: EDIT,
      quoteId: quoteEditorState.quoteId,
      onClose: handleQuoteEditorClose,
      embedSource: EMBED_SOURCE.QUOTE_PREVIEW_PANEL
    });
  };
  const renderInvoiceEditor = () => {
    return isScopedForInvoices ? /*#__PURE__*/_jsx(InvoicesEditorIFrameModal, {
      show: invoiceEditorState.open,
      objectId: invoiceEditorState.quoteId,
      onClose: handleInvoiceEditorClose,
      objectTypeId: QUOTE_TYPE_ID,
      operation: InvoiceOperation.CONVERT_TO_INVOICE
    }) : null;
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Flex, {
      justifyContent: "end",
      children: /*#__PURE__*/_jsx(ActionsDropdown, {
        dropdownProps: {
          buttonUse: 'transparent',
          placement: 'bottom right',
          menuWidth: 200
        },
        quote: quote,
        trackingLocation: TRACKING_LOCATION,
        userEmail: userEmail,
        fullQuoteUrl: fullQuoteUrl,
        quoteSigners: quoteSigners ? quoteSigners.toJS() : null,
        canCurrentUserEditQuote: canCurrentUserEditQuote,
        isScopedForInvoices: isScopedForInvoices,
        isScopedForBilling: isScopedForBilling,
        scopes: new Set(scopes),
        isUngatedForSeamlessSending: isUngatedForSeamlessSending,
        isUngatedForQuoteTracking: isUngatedForQuoteTracking,
        isUngatedForAiGenerateBuyerEmail: isUngatedForAiGenerateBuyerEmail,
        userOwnerId: userOwnerId,
        showQuoteEditor: showQuoteEditor,
        showInvoiceEditor: showInvoiceEditor,
        onQuotePropertyUpdate: handleQuotePropertyUpdate,
        onQuoteDelete: handleQuoteDelete
      })
    }), renderQuoteEditor(), renderInvoiceEditor()]
  });
};
export default QuoteHighlightActions;